import useGetCanBulkExport from '@/api/queries/me/useGetCanBulkExport';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { useCanUpdateAccount } from '@/hooks/useCanUpdateAccount';
import CompanySettingsBranding from '@/ui/components/companySettings/CompanySettingsBranding';
import CompanySettingsCompanyInfo from '@/ui/components/companySettings/CompanySettingsCompanyInfo';
import { CompanySettingsCustomerCustomFields } from '@/ui/components/companySettings/CompanySettingsCustomerCustomFields';
import CompanySettingsDataExport from '@/ui/components/companySettings/CompanySettingsDataExport';
import CompanySettingsEmailCustomisation from '@/ui/components/companySettings/CompanySettingsEmailCustomisation';
import CompanySettingsGettingPaid from '@/ui/components/companySettings/CompanySettingsGettingPaid';
import CompanySettingsPrimaryPipeline from '@/ui/components/companySettings/CompanySettingsPrimaryPipeline';
import { CompanySettingsProjectCustomFields } from '@/ui/components/companySettings/CompanySettingsProjectCustomFields';
import CompanySettingsSchedule from '@/ui/components/companySettings/CompanySettingsSchedule';
import CompanySettingsTaxRates from '@/ui/components/companySettings/CompanySettingsTaxRates';
import CompanySettingsTerms from '@/ui/components/companySettings/CompanySettingsTerms';
import CompanySettingsTimeTracking from '@/ui/components/companySettings/CompanySettingsTimeTracking';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { EstimatesPermissions } from '@payaca/permissions/estimates/estimates.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import React, { FC, type JSX } from 'react';
import { useHistory, useParams } from 'react-router';
import { useSelector } from '../../../api/state';
import { getUserRoles } from '../../../utils/stateAccessors';
import CompanySettingsEstimates from '../../components/companySettings/CompanySettingsEstimates';
import CompanySettingsFieldServiceCustomFields from '../../components/companySettings/CompanySettingsFieldServiceCustomFields';
import CompanySettingsFieldServiceSuppliers from '../../components/companySettings/CompanySettingsFieldServiceSuppliers';
import CompanySettingsIntegrations from '../../components/companySettings/CompanySettingsIntegrations';
import CompanySettingsTabBase from '../../components/companySettings/CompanySettingsTabBase';
import TabbedContentPageWrapper from '../pageWrappers/tabbedContentPageWrapper/TabbedContentPageWrapper';
import './CompanySettingsPage.sass';
type TabDescriptor = React.ComponentProps<
  typeof TabbedContentPageWrapper
>['tabbedContentConfig']['tabs'][number] & {
  urlSlug: string;
};

const CompanySettingsPage: FC = (): JSX.Element => {
  const isAdmin = useCanUpdateAccount();
  const hasEstimates = useMyAccountHasFeature('hasEstimates');
  const userRoles = useSelector(getUserRoles);
  const { canBulkExport } = useGetCanBulkExport();

  const tabs: Array<TabDescriptor> = [
    {
      title: 'Company info',
      label: 'Company info',
      content: <CompanySettingsCompanyInfo readOnly={!isAdmin} />,
      urlSlug: 'company-info',
    },
    {
      title: 'Branding',
      label: 'Branding',
      content: <CompanySettingsBranding readOnly={!isAdmin} />,
      urlSlug: 'branding',
    },
    {
      title: 'Getting paid',
      label: 'Getting paid',
      content: <CompanySettingsGettingPaid readOnly={!isAdmin} />,
      urlSlug: 'getting-paid',
    },
    {
      title: 'Tax rates',
      label: 'Tax rates',
      content: <CompanySettingsTaxRates readOnly={!isAdmin} />,
      urlSlug: 'tax-rates',
    },
    {
      title: 'Terms',
      label: 'Terms',
      content: <CompanySettingsTerms readOnly={!isAdmin} />,
      urlSlug: 'terms',
    },
    {
      title: 'Schedule',
      label: 'Schedule',
      content: <CompanySettingsSchedule readOnly={!isAdmin} />,
      urlSlug: 'schedule',
    },
    {
      title: 'Email customisation',
      label: 'Email customisation',
      content: <CompanySettingsEmailCustomisation readOnly={!isAdmin} />,
      urlSlug: 'email-customisation',
    },
    {
      title: 'Custom fields',
      label: 'Custom fields',
      urlSlug: 'custom-fields',
      content: (
        <CompanySettingsTabBase>
          <CompanySettingsPrimaryPipeline readOnly={!isAdmin} />
          <PermissionGuard
            renderIfHasPermissions={[
              AccountsPermissions.MANAGE_CUSTOM_FIELD_GROUPS,
            ]}
          >
            <CompanySettingsProjectCustomFields />
            <CompanySettingsCustomerCustomFields />
          </PermissionGuard>
          <CompanySettingsTimeTracking readOnly={!isAdmin} />
        </CompanySettingsTabBase>
      ),
    },
    {
      title: 'Field service',
      label: 'Field service',
      urlSlug: 'field-service',
      content: (
        <CompanySettingsTabBase>
          <CompanySettingsFieldServiceSuppliers readOnly={!isAdmin} />
          <CompanySettingsFieldServiceCustomFields readOnly={!isAdmin} />
        </CompanySettingsTabBase>
      ),
    },
  ];

  if (
    hasEstimates &&
    userHasRequiredPermission(userRoles, [
      EstimatesPermissions.MANAGE_PREFERRED_PRODUCTS,
      EstimatesPermissions.GET_DEFAULT_ITEMS,
    ])
  ) {
    tabs.push({
      title: 'Estimates',
      label: 'Estimates',
      urlSlug: 'estimates',
      content: (
        <CompanySettingsTabBase>
          <CompanySettingsEstimates />
        </CompanySettingsTabBase>
      ),
    });
  }

  if (
    userHasRequiredPermission(userRoles, [
      AccountsPermissions.MANAGE_API_KEYS,
      AccountsPermissions.MANAGE_WEBHOOKS,
    ])
  ) {
    tabs.push({
      title: 'Integrations',
      label: 'Integrations',
      urlSlug: 'integrations',
      content: (
        <CompanySettingsTabBase>
          <CompanySettingsIntegrations />
        </CompanySettingsTabBase>
      ),
    });
  }

  if (canBulkExport) {
    tabs.push({
      title: 'Data export',
      label: 'Data export',
      urlSlug: 'data-export',
      content: (
        <CompanySettingsTabBase>
          <CompanySettingsDataExport />
        </CompanySettingsTabBase>
      ),
    });
  }

  const { tab: activeTabRouteParam } = useParams<{
    tab?: string;
  }>();

  const history = useHistory();

  const { searchParams: urlSearchParams } = new URL(window.location.href);

  if (!activeTabRouteParam) {
    history.replace(
      `/companySettings/company-info?${urlSearchParams.toString()}`
    );
  }

  const activeTabLabel = tabs.find(
    (tab) => tab.urlSlug === activeTabRouteParam
  )?.label;

  const handleChangeTab = (newActiveTabLabel: string) => {
    const urlSlug = tabs.find(
      (tab) => tab.label === newActiveTabLabel
    )?.urlSlug;
    if (urlSlug) {
      history.push(`/companySettings/${urlSlug}?${urlSearchParams.toString()}`);
    }
  };

  return (
    <TabbedContentPageWrapper
      className="company-settings-page"
      tabbedContentConfig={{
        tabs: tabs,
        onChangeTab: handleChangeTab,
        activeTabLabel: activeTabLabel,
      }}
    />
  );
};

export default CompanySettingsPage;
